import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { LEAF_COLORS, LeafRosette } from "@/components/landing/GeometricTree";
import { useContent } from "@/context/ContentContext";

const DEFAULT_PARAGRAPHS = [
  "786 Interactive is a software studio dedicated to the Islamic world. We design and engineer web and mobile applications — prayer companions, e-learning madrasahs, halal directories, zakat tools and bespoke platforms — combining modern engineering with the timeless beauty of Islamic art and the tranquility of nature.",
  "The name 786 reflects how we begin every build: Bismillah. From the first sketch to the final release, our work is guided by purpose, precision and a deep understanding of the communities we serve.",
];

const DEFAULT_VALUES = [
  { title: "Ihsan", sub: "Excellence in craft", desc: "We build as if it will be seen — because it will. Every pixel, every line of code is finished with care." },
  { title: "Amanah", sub: "Trust & integrity", desc: "Your project is a trust. Transparent timelines, honest pricing, and software that respects its users." },
  { title: "Khidmah", sub: "Service to community", desc: "Technology in service of the Ummah — tools that make worship, learning and community life easier." },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { content } = useContent();
  const a = content?.about;
  const paragraphs = a?.paragraphs?.length ? a.paragraphs : DEFAULT_PARAGRAPHS;
  const values = a?.values?.length ? a.values : DEFAULT_VALUES;

  return (
    <>
      <Helmet>
        <title>About Us | 786 Interactive — Islamic App Development Studio</title>
        <meta
          name="description"
          content="786 Interactive is a software studio dedicated to the Islamic world. We build web and mobile applications guided by Ihsan, Amanah and Khidmah — excellence, trust and service to the Ummah."
        />
        <link rel="canonical" href={`${window.location.origin}/about`} />
        <meta property="og:title" content="About 786 Interactive — Islamic App Development Studio" />
        <meta property="og:description" content="Built in service of the Ummah. We craft prayer apps, e-learning platforms and bespoke Islamic digital products with care and excellence." />
        <meta property="og:url" content={`${window.location.origin}/about`} />
        <meta name="twitter:title" content="About 786 Interactive" />
        <meta name="twitter:description" content="Built in service of the Ummah — web and mobile apps crafted with Ihsan, Amanah and Khidmah." />
      </Helmet>
      <main data-testid="about-page" className="bg-[#F7F6F1] text-[#122A1D] min-h-screen">
      <Header />

      <section className="pt-36 pb-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <p className="uppercase tracking-[0.35em] text-xs text-[#8C6A1F] mb-6 flex items-center gap-4">
          <span className="w-12 h-px bg-[#A9842F]/70" />
          {a?.eyebrow || "About us"}
        </p>
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02]">
              {a?.heading || "Built in service of the"}{" "}
              <em className="text-[#A9842F]">{a?.heading_accent || "Ummah"}</em>
            </h1>
            {paragraphs.map((para, i) => (
              <p key={i} className="mt-5 text-[#44584C] font-light leading-relaxed max-w-2xl">
                {para}
              </p>
            ))}
          </div>
          <div className="hidden lg:flex justify-center" aria-hidden="true">
            <svg viewBox="0 0 200 200" className="w-64 h-64">
              <defs>
                {LEAF_COLORS.map(([c1, c2], i) => (
                  <linearGradient key={i} id={`leafGa${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c1} />
                    <stop offset="100%" stopColor={c2} />
                  </linearGradient>
                ))}
              </defs>
              <LeafRosette cx={100} cy={100} count={10} dist={42} scale={2.2} dir="geo-ccw" palette={[0, 1, 2]} center={false} />
              <LeafRosette cx={100} cy={100} count={8} dist={18} scale={1.4} dir="geo-cw" start={18} palette={[2, 3, 1]} />
            </svg>
          </div>
        </div>
      </section>

      <section className="pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-5">
          {values.map((v) => (
            <div
              key={v.title}
              data-testid={`about-value-${v.title.toLowerCase()}`}
              className="bg-white border border-[#122A1D]/10 shadow-[0_8px_30px_rgba(18,42,29,0.06)] p-8"
            >
              <h3 className="font-heading text-3xl text-[#122A1D]">{v.title}</h3>
              <p className="uppercase tracking-[0.25em] text-[11px] text-[#8C6A1F] mt-1 mb-4">{v.sub}</p>
              <p className="text-[#5B6F64] font-light text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-[#122A1D] p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading text-3xl lg:text-4xl text-[#F5F0E6]">
              {a?.cta_heading || "Have a project in mind?"}
            </h2>
            <p className="text-[#A3B8AD] font-light mt-3 max-w-lg">
              {a?.cta_blurb || "Whether it's a masjid app or a full-scale platform, we'd love to hear your vision."}
            </p>
          </div>
          <a
            href="/#contact"
            data-testid="about-cta-contact"
            className="bg-[#C8A24A] text-[#0B1F16] hover:bg-[#B8923D] font-semibold transition-colors duration-300 px-8 py-4 uppercase tracking-wider text-sm shrink-0"
          >
            {a?.cta_button || "Get in touch"}
          </a>
        </div>
      </section>

      <Footer />
    </main>
    </>
  );
}
